import React, { useState } from 'react';
import SEO from '../components/SEO';
import SearchForm from '../components/SearchForm';
import AirlineCallPopup from '../components/AirlineCallPopup';

const TFN = '+1 (855) 895-8503';
const TEL = 'tel:+18558958503';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'United Airlines Flights – Book & Save | Flyairlinebooking',
  url: 'https://flyairlinebooking.com/ua/airlines',
  description:
    'Book United Airlines flights at the best prices. Compare fares, explore routes, and get 24/7 support from Flyairlinebooking travel experts.',
  publisher: {
    '@type': 'Organization',
    name: 'Flyairlinebooking',
    url: 'https://flyairlinebooking.com',
    telephone: TFN,
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How do I book United Airlines flights through Flyairlinebooking?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `You can book online at flyairlinebooking.com or call our 24/7 support line at ${TFN}. Our agents search all available United fares to find you the best price.`,
      },
    },
    {
      '@type': 'Question',
      name: 'What is United Airlines MileagePlus?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'MileagePlus is United Airlines\' frequent flyer program. Members earn miles on United and partner flights which can be redeemed for award tickets, seat upgrades, hotel stays, and more.',
      },
    },
    {
      '@type': 'Question',
      name: 'What cabin classes does United Airlines offer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'United Airlines offers United Economy, Economy Plus (extra legroom), United First (domestic), United Business, and Polaris Business Class on long-haul international routes.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I change or cancel my United Airlines ticket?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'United Airlines allows free changes and cancellations on most fare types. Basic Economy fares may have restrictions. Call us at ' + TFN + ' and we will walk you through your options.',
      },
    },
    {
      '@type': 'Question',
      name: 'What are United Airlines\' main hubs?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'United Airlines operates major hubs at Chicago O\'Hare (ORD), Denver (DEN), Houston George Bush (IAH), Los Angeles (LAX), Newark (EWR), San Francisco (SFO), and Washington Dulles (IAD).',
      },
    },
  ],
};

const ROUTES = [
  { from: 'New York (EWR)', to: 'Los Angeles (LAX)', duration: '5h 30m', tag: 'Popular' },
  { from: 'Chicago (ORD)', to: 'Miami (MIA)', duration: '2h 55m', tag: 'Popular' },
  { from: 'San Francisco (SFO)', to: 'New York (JFK)', duration: '5h 20m', tag: 'Business' },
  { from: 'Denver (DEN)', to: 'Las Vegas (LAS)', duration: '1h 55m', tag: 'Deal' },
  { from: 'Houston (IAH)', to: 'Cancun (CUN)', duration: '2h 10m', tag: 'Vacation' },
  { from: 'Los Angeles (LAX)', to: 'Tokyo (NRT)', duration: '11h 30m', tag: 'International' },
  { from: 'Newark (EWR)', to: 'London (LHR)', duration: '7h 15m', tag: 'International' },
  { from: 'Washington (IAD)', to: 'Orlando (MCO)', duration: '2h 05m', tag: 'Deal' },
];

const CABINS = [
  {
    name: 'United Economy',
    icon: '💺',
    color: 'border-blue-200 bg-blue-50',
    badge: 'bg-blue-100 text-blue-800',
    features: ['Standard seat pitch', 'Carry-on bag included', 'In-seat entertainment', 'Meals on long-haul routes'],
  },
  {
    name: 'Economy Plus',
    icon: '🪑',
    color: 'border-indigo-200 bg-indigo-50',
    badge: 'bg-indigo-100 text-indigo-800',
    features: ['Up to 6" extra legroom', 'Preferred boarding', 'Dedicated overhead bin access', 'Priority customer service'],
  },
  {
    name: 'United First',
    icon: '⭐',
    color: 'border-amber-200 bg-amber-50',
    badge: 'bg-amber-100 text-amber-800',
    features: ['Fully reclining seats (select routes)', 'Premium meal service', 'Priority check-in & boarding', 'Two free checked bags'],
  },
  {
    name: 'Polaris Business',
    icon: '🌟',
    color: 'border-purple-200 bg-purple-50',
    badge: 'bg-purple-100 text-purple-800',
    features: ['Lie-flat bed seat', 'Saks Fifth Avenue bedding', 'Multi-course dining', 'Exclusive Polaris lounge access'],
  },
];

const FAQS = [
  {
    q: 'How do I book United Airlines flights through Flyairlinebooking?',
    a: `You can book online or call our 24/7 support line at ${TFN}. Our agents search all available United fares to get you the best price.`,
  },
  {
    q: 'What is United Airlines MileagePlus?',
    a: "MileagePlus is United's frequent flyer program. Earn miles on United and 40+ partner airlines, then redeem them for award flights, upgrades, hotels, and more.",
  },
  {
    q: 'What cabin classes does United Airlines offer?',
    a: 'United offers Economy, Economy Plus (extra legroom), United First (domestic), United Business, and Polaris Business Class on long-haul international routes.',
  },
  {
    q: 'Can I change or cancel my United Airlines ticket?',
    a: `United allows free changes and cancellations on most fare types. Basic Economy fares may have restrictions. Call us at ${TFN} and we will walk you through your options at no extra charge.`,
  },
  {
    q: "What are United Airlines' main hubs?",
    a: "United's major hubs are Chicago O'Hare (ORD), Denver (DEN), Houston Bush (IAH), Los Angeles (LAX), Newark (EWR), San Francisco (SFO), and Washington Dulles (IAD).",
  },
];

export default function UnitedAirlinesPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const today = new Date();
  today.setDate(today.getDate() + 14);
  const date = today.toISOString().split('T')[0];
  const searchUrl = `/flights?origin=ORD&destination=LAX&date=${date}&passengers=1&class=economy`;

  return (
    <div className="bg-white">
      <AirlineCallPopup
        name="United Airlines"
        code="ua"
        logo={{ src: '/united-airlines-logo.png', alt: 'United Airlines logo' }}
      />

      {/* Trademark Disclaimer */}
      <div className="bg-amber-50 border-b border-amber-100 py-2.5 px-4 text-center text-xs text-amber-800">
        <strong>Disclaimer:</strong> Flyairlinebooking is an independent travel agency. &quot;United Airlines&quot; and the United logo are trademarks of United Airlines, Inc. We are not affiliated with or endorsed by United Airlines.
      </div>

      <SEO
        title="United Airlines Flights – Book Cheap UA Tickets"
        canonical="/ua/airlines"
        description="Book United Airlines (UA) flights at the lowest fares. Compare Economy, Economy Plus, and Polaris Business Class tickets. 24/7 expert support — call +1 (855) 895-8503."
        keywords="United Airlines flights, UA flights, United Airlines booking, cheap United Airlines tickets, MileagePlus, Polaris business class, United Airlines deals, book United flights"
        jsonLd={[jsonLd, faqJsonLd]}
      />

      {/* ── Hero ── */}
      <div className="relative bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}
        />
        <div className="relative max-w-5xl mx-auto px-4 pt-12 pb-10 sm:pt-16 sm:pb-12">
          {/* Logo + tagline */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 mb-8">
            <div className="bg-white rounded-2xl p-3 shadow-xl shrink-0">
              <img
                src="/united-airlines-logo.png"
                alt="United Airlines logo"
                width="80"
                height="80"
                className="block"
              />
            </div>
            <div className="text-center sm:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-medium mb-3">
                ✈️ Star Alliance Member · IATA: UA
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
                United Airlines Flights<br className="hidden sm:block" />
                <span className="text-blue-300">Book at the Best Price</span>
              </h1>
              <p className="text-blue-100 text-base sm:text-lg mt-3 max-w-xl">
                Economy to Polaris Business Class · Domestic & International · 24/7 Expert Support
              </p>
            </div>
          </div>

          {/* ── Embedded Search Form ── */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 sm:p-7">
            <p className="text-white/70 text-sm font-medium mb-4">Search United Airlines Flights</p>
            <SearchForm initialValues={{ origin: 'ORD' }} />
          </div>

          {/* Phone CTA below form */}
          <div className="mt-5 text-center">
            <p className="text-blue-200 text-sm">
              Prefer to book by phone?{' '}
              <a href={TEL} className="text-white font-bold hover:text-blue-200 underline underline-offset-2">
                Call {TFN}
              </a>
              {' '}— available 24/7
            </p>
          </div>
        </div>
      </div>

      {/* ── Stats strip ── */}
      <div className="bg-white border-b border-gray-100 py-6 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          {[
            { value: '1926', label: 'Founded' },
            { value: '800+', label: 'Aircraft' },
            { value: '340+', label: 'Destinations' },
            { value: 'Star Alliance', label: 'Global Network' },
          ].map(s => (
            <div key={s.label}>
              <p className="text-2xl font-extrabold text-blue-800">{s.value}</p>
              <p className="text-gray-500 text-sm">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── About United ── */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4">
              About United Airlines
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              United Airlines is one of the world's largest airlines and a founding member of the <strong>Star Alliance</strong> — the globe's largest airline alliance with 26 member carriers. Operating from seven major US hubs, United connects travelers to <strong>340+ destinations</strong> in more than 60 countries.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              United is renowned for its <strong>Polaris Business Class</strong> — a lie-flat seat product on international long-haul flights — and its <strong>MileagePlus</strong> frequent flyer program, consistently ranked as one of the most valuable in the industry.
            </p>
            <ul className="space-y-2">
              {[
                'Largest Star Alliance member airline',
                'Polaris lie-flat seats on international routes',
                'MileagePlus with 100M+ members',
                'Free same-day flight changes on most fares',
                'United Club lounges at 45+ airports',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-blue-600 font-bold mt-0.5">✓</span> {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-gradient-to-br from-blue-900 to-blue-700 rounded-2xl p-7 text-white">
            <h3 className="text-lg font-bold mb-5">Key Hubs</h3>
            <div className="space-y-3">
              {[
                { code: 'ORD', name: "Chicago O'Hare" },
                { code: 'EWR', name: 'Newark Liberty' },
                { code: 'IAH', name: 'Houston Bush' },
                { code: 'SFO', name: 'San Francisco' },
                { code: 'LAX', name: 'Los Angeles' },
                { code: 'DEN', name: 'Denver' },
                { code: 'IAD', name: 'Washington Dulles' },
              ].map(hub => (
                <div key={hub.code} className="flex items-center justify-between border-b border-white/10 pb-2 last:border-0 last:pb-0">
                  <span className="text-blue-200 text-sm">{hub.name}</span>
                  <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded">{hub.code}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Cabin Classes ── */}
      <section className="bg-gray-50 py-12 sm:py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">United Airlines Cabin Classes</h2>
            <p className="text-gray-500">From budget-friendly Economy to lie-flat Polaris — find the right seat for your journey.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CABINS.map(cabin => (
              <div key={cabin.name} className={`border-2 ${cabin.color} rounded-2xl p-5 flex flex-col`}>
                <div className="text-3xl mb-3">{cabin.icon}</div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full self-start mb-3 ${cabin.badge}`}>
                  {cabin.name}
                </span>
                <ul className="space-y-2 flex-grow">
                  {cabin.features.map(f => (
                    <li key={f} className="text-xs text-gray-700 flex items-start gap-1.5">
                      <span className="text-blue-500 font-bold shrink-0">✓</span> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Popular Routes ── */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Popular United Airlines Routes</h2>
          <p className="text-gray-500">Frequently booked United Airlines routes — call us for the best available fare.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {ROUTES.map((route, i) => (
            <a
              key={i}
              href={searchUrl}
              className="flex items-center justify-between bg-white border border-gray-100 shadow-sm hover:shadow-md rounded-xl px-5 py-4 transition-shadow group"
            >
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <span>{route.from}</span>
                  <span className="text-gray-400">→</span>
                  <span>{route.to}</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">✈️ {route.duration}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  route.tag === 'Deal' ? 'bg-green-100 text-green-700' :
                  route.tag === 'International' ? 'bg-purple-100 text-purple-700' :
                  route.tag === 'Business' ? 'bg-amber-100 text-amber-700' :
                  route.tag === 'Vacation' ? 'bg-pink-100 text-pink-700' :
                  'bg-blue-100 text-blue-700'
                }`}>{route.tag}</span>
                <span className="text-gray-300 group-hover:text-blue-500 transition-colors">→</span>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">Ready to Book Your United Airlines Flight?</h2>
          <p className="text-blue-200 mb-7 text-base">
            Our travel experts are available 24/7 to find you the best United fares, help with seat upgrades, and assist with any changes or cancellations.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={TEL}
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 font-bold px-8 py-3.5 rounded-xl hover:bg-blue-50 transition-colors"
            >
              📞 Call {TFN}
            </a>
            <a
              href={searchUrl}
              className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white/60 text-white font-semibold px-8 py-3.5 rounded-xl transition-colors"
            >
              Search Flights Online →
            </a>
          </div>
          <p className="text-blue-300 text-xs mt-5">No booking fees · Free 24/7 support · Best price guarantee</p>
        </div>
      </section>

      {/* ── Why Book With Us ── */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Why Book United Flights with Flyairlinebooking?</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: '💰', title: 'Best Fare Search', desc: 'We compare all available United fares — published, web-only, and consolidator — to find your lowest price.' },
            { icon: '📞', title: '24/7 Live Agents', desc: 'Real travel experts answer your call any time, day or night. No bots, no hold music, just fast help.' },
            { icon: '🔄', title: 'Free Change Assistance', desc: 'Need to reschedule? We handle United flight change requests on your behalf at no additional service charge.' },
            { icon: '🪑', title: 'Seat Selection Help', desc: 'We assist with seat upgrades, Economy Plus selection, and special meal requests on your United booking.' },
            { icon: '🌐', title: 'MileagePlus Support', desc: 'Guidance on earning and redeeming MileagePlus miles, status matching, and upgrade awards.' },
            { icon: '🛡️', title: 'Booking Protection', desc: 'We monitor your itinerary for schedule changes and proactively notify you of any disruptions.' },
          ].map(item => (
            <div key={item.title} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <div className="text-3xl mb-3">{item.icon}</div>
              <h3 className="font-bold text-gray-900 mb-1.5">{item.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-gray-50 py-12 sm:py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Frequently Asked Questions</h2>
            <p className="text-gray-500">Everything you need to know about booking United Airlines flights.</p>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left px-5 py-4 flex items-start justify-between gap-3 hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-800 text-sm leading-snug">{faq.q}</span>
                  <span className={`text-blue-500 font-bold text-lg shrink-0 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-50">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="max-w-5xl mx-auto px-4 py-10 text-center">
        <p className="text-gray-500 text-sm">
          Have more questions about United Airlines flights?{' '}
          <a href={TEL} className="text-blue-600 font-semibold hover:underline">{TFN}</a>
          {' '}— we are available 24/7.
        </p>
      </section>
    </div>
  );
}
